import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useQuery, useMutation } from '@apollo/client';

import { captureException } from '@playbookux/playbook-web-core';
import Cell from './cell';

import { GET_SIMILARITY_MATRIX, PROCESS_SIMILARITY_MATRIX } from '../../graphql';

import {
  Container,
  ContainerHeader,
  ContainerTable,
  Content,
  GradientBar,
  HeaderText,
  Loader,
  RowContainer,
  RowTitle,
} from './styles';

const POLL_INTERVAL = 3000;

const SimilarityMatrix = (props) => {
  const {
    project: {
      id,
      audienceParticipants,
    },
    segmentId,
  } = props;

  const {
    data: { cardSortAnalytics } = {},
    stopPolling,
  } = useQuery(GET_SIMILARITY_MATRIX, {
    variables: {
      projectId: id,
      segmentId,
    },
    pollInterval: POLL_INTERVAL,
  });

  const [processSimilarityMatrix] = useMutation(PROCESS_SIMILARITY_MATRIX);

  const {
    similarityMatrix,
    similarityMatrixLabels: labels,
  } = cardSortAnalytics || {};

  const [similarityMatrixVal, setSimilarityMatrixVal] = useState({});
  const [similarityMatrixLabels, setSimilarityMatrixLabels] = useState({});
  const [currentCol, setCurrentCol] = useState(null);

  useEffect(() => {
    try {
      if (similarityMatrix) {
        setSimilarityMatrixVal(JSON.parse(similarityMatrix));
        setSimilarityMatrixLabels(JSON.parse(labels));
        stopPolling();
      } else {
        processSimilarityMatrix({
          variables: {
            projectId: id,
          },
        });
      }
    } catch (ex) {
      captureException('SIMILARITY_MATRIX_USE_EFFECT_FAILED', ex);
    }
  }, [
    similarityMatrix,
    labels,
    id,
    setSimilarityMatrixVal,
    setSimilarityMatrixLabels,
    stopPolling,
    processSimilarityMatrix,
  ]);

  const rowCount = useMemo(() => Object.keys(similarityMatrixVal).length, [similarityMatrixVal]);

  const handleChangeHover = (col) => {
    setCurrentCol(col);
  };

  if (!rowCount) {
    return (
      <Container>
        <Loader data-loader="similarity-matrix-loader" />
      </Container>
    );
  }

  return (
    <Container>
      <ContainerHeader>
        <h2> Similarity Matrix </h2>
        <Content>
          <HeaderText>Total Participants</HeaderText>
          0%&nbsp;
          <GradientBar />
          &nbsp;100% (
          {audienceParticipants}
          {' '}
          participants)
        </Content>
      </ContainerHeader>
      <ContainerTable>
        {
          Object.keys(similarityMatrixVal).map((col, keyIndex) => (
            <RowContainer>
              {
                Object.keys(similarityMatrixVal[col]).map((row, rowIndex) => (
                  keyIndex > rowIndex && (
                    <Cell
                      value={similarityMatrixVal[col][row]}
                      col={similarityMatrixLabels[col]}
                      row={similarityMatrixLabels[row]}
                      total={audienceParticipants}
                      onChangeVisible={handleChangeHover}
                    />
                  )
                ))
              }
              <RowTitle highlight={currentCol === col}>{similarityMatrixLabels[col]}</RowTitle>
            </RowContainer>
          ))
        }
      </ContainerTable>
    </Container>
  );
};

SimilarityMatrix.propTypes = {
  project: PropTypes.shape({
    id: PropTypes.string.isRequired,
    audienceParticipants: PropTypes.string.isRequired,
  }).isRequired,
  segmentId: PropTypes.string,
};

SimilarityMatrix.defaultProps = {
  segmentId: undefined,
};

export default SimilarityMatrix;
